import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Camera, XCircle, Music, CheckCircle, AlertCircle } from 'lucide-react';

export default function SpotifyQRScanner() {
  const [scanning, setScanning] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);
  const [accessToken, setAccessToken] = useState('');
  const [playbackStarted, setPlaybackStarted] = useState(false);
  const [error, setError] = useState('');
  const [cameraLoading, setCameraLoading] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const scanIntervalRef = useRef(null);
  const lastScannedRef = useRef('');

  // Spotify OAuth configuration
  // Replace this with your Spotify Client ID
  const CLIENT_ID = process.env.REACT_APP_SPOTIFY_CLIENT_ID;
  const REDIRECT_URI = 'https://spot-qr-scan.pages.dev'; //
  const SCOPES = 'user-modify-playback-state user-read-playback-state';

  // PKCE helper functions
  const generateRandomString = (length) => {
    const possible = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    const values = crypto.getRandomValues(new Uint8Array(length));
    return values.reduce((acc, x) => acc + possible[x % possible.length], "");
  };

  const sha256 = async (plain) => {
    const encoder = new TextEncoder();
    const data = encoder.encode(plain);
    return window.crypto.subtle.digest('SHA-256', data);
  };

  const base64encode = (input) => {
    return btoa(String.fromCharCode(...new Uint8Array(input)))
      .replace(/=/g, '')
      .replace(/\+/g, '-')
      .replace(/\//g, '_');
  };

  // Extract track ID from Spotify URL
  const extractTrackId = (url) => {
    try {
      const trackMatch = url.match(/spotify\.com\/track\/([a-zA-Z0-9]+)/);
      if (trackMatch) return trackMatch[1];
      
      const uriMatch = url.match(/spotify:track:([a-zA-Z0-9]+)/);
      if (uriMatch) return uriMatch[1];
      
      return null;
    } catch (e) {
      return null;
    }
  };

  // Play track on Spotify
  const playTrack = async (trackId) => {
    // Prevent duplicate scans
    if (trackId === lastScannedRef.current) {
      return;
    }
    lastScannedRef.current = trackId;

    try {
      const response = await fetch('https://api.spotify.com/v1/me/player/play', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          uris: [`spotify:track:${trackId}`]
        })
      });

      if (response.status === 204 || response.status === 200) {
        setPlaybackStarted(true);
        setError('');
        setTimeout(() => {
          setPlaybackStarted(false);
          lastScannedRef.current = '';
        }, 2000);
        return true;
      } else if (response.status === 404) {
        setError('No active Spotify device found. Please open Spotify and play a song first.');
        lastScannedRef.current = '';
        return false;
      } else if (response.status === 403) {
        setError('Premium account required for playback control.');
        lastScannedRef.current = '';
        return false;
      } else {
        const data = await response.json();
        setError(data.error?.message || 'Failed to play track');
        lastScannedRef.current = '';
        return false;
      }
    } catch (err) {
      setError('Error playing track: ' + err.message);
      lastScannedRef.current = '';
      return false;
    }
  };

  // Spotify OAuth login with PKCE
  const loginToSpotify = async () => {
    const codeVerifier = generateRandomString(64);
    const hashed = await sha256(codeVerifier);
    const codeChallenge = base64encode(hashed);

    // Store code verifier for later use
    window.localStorage.setItem('code_verifier', codeVerifier);

    const authUrl = new URL('https://accounts.spotify.com/authorize');
    const params = {
      client_id: CLIENT_ID,
      response_type: 'code',
      redirect_uri: REDIRECT_URI,
      scope: SCOPES,
      code_challenge_method: 'S256',
      code_challenge: codeChallenge,
    };

    authUrl.search = new URLSearchParams(params).toString();
    window.location.href = authUrl.toString();
  };

  // Exchange code for token
  const getToken = useCallback(async (code) => {
    const codeVerifier = window.localStorage.getItem('code_verifier');

    const response = await fetch('https://accounts.spotify.com/api/token', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({
        client_id: CLIENT_ID,
        grant_type: 'authorization_code',
        code: code,
        redirect_uri: REDIRECT_URI,
        code_verifier: codeVerifier,
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to get access token');
    }

    const data = await response.json();
    
    // Save tokens to localStorage
    if (data.access_token) {
      window.localStorage.setItem('spotify_access_token', data.access_token);
      window.localStorage.setItem('spotify_token_expiry', Date.now() + (data.expires_in * 1000));
    }
    if (data.refresh_token) {
      window.localStorage.setItem('spotify_refresh_token', data.refresh_token);
    }
    
    return data;
  }, [CLIENT_ID, REDIRECT_URI]);

  // ...existing code...

  // Check for authorization code or access token
  useEffect(() => {
    const processAuth = async () => {
      // Check for authorization code (PKCE flow)
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      
      console.log('Authorization code:', code ? 'Yes' : 'No'); // Debug

      if (code) {
        try {
          const tokenData = await getToken(code);
          console.log('Token received:', tokenData.access_token ? 'Yes' : 'No'); // Debug
          setAccessToken(tokenData.access_token);
          setAuthenticated(true);
          window.localStorage.removeItem('code_verifier');
          window.history.replaceState({}, document.title, window.location.pathname);
        } catch (error) {
          console.error('Token exchange failed:', error);
          setError('Authentication failed. Please try again.');
        }
      }

      // Also check hash for old flow compatibility
      const hash = window.location.hash;
      console.log('URL hash:', hash); // Debug
      if (hash) {
        const params = new URLSearchParams(hash.substring(1));
        const token = params.get('access_token');
        console.log('Token found in hash:', token ? 'Yes' : 'No'); // Debug
        if (token) {
          setAccessToken(token);
          setAuthenticated(true);
          window.history.replaceState({}, document.title, window.location.pathname);
        }
      }
    };

    processAuth();
  }, [getToken]);

  const startScanning = async () => {
    try {
      setError('');
      setCameraLoading(true);

      // Set scanning to true first so the video element renders
      setScanning(true);

      // Wait for next tick to ensure video element is rendered
      await new Promise(resolve => setTimeout(resolve, 100));

      if (!videoRef.current) {
        console.error('Video element still not ready after delay');
        setError('Camera not ready. Please try again.');
        setScanning(false);
        setCameraLoading(false);
        return;
      }

      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
        videoRef.current.srcObject = stream;
        streamRef.current = stream;
        await videoRef.current.play();

        scanIntervalRef.current = setInterval(() => {
          scanQRCode();
        }, 500);
      } catch (playErr) {
        console.error('Video play() failed:', playErr);
        setCameraLoading(false);
        setError('Failed to start video: ' + playErr.message);
      }
    } catch (err) {
      console.error('Camera error:', err);
      setScanning(false);
      setCameraLoading(false);
      setError(`Camera error: ${err.name} - ${err.message}. Please check camera permissions.`);
    }
  };

  const stopScanning = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (scanIntervalRef.current) {
      clearInterval(scanIntervalRef.current);
    }
    setScanning(false);
    lastScannedRef.current = '';
  };

  const logout = () => {
    // Clear all tokens
    window.localStorage.removeItem('spotify_access_token');
    window.localStorage.removeItem('spotify_token_expiry');
    window.localStorage.removeItem('spotify_refresh_token');
    window.localStorage.removeItem('code_verifier');
    
    // Stop scanning if active
    stopScanning();
    
    // Reset state
    setAccessToken('');
    setAuthenticated(false);
    setError('');
  };

  const scanQRCode = () => {
    if (!videoRef.current || !canvasRef.current) {
      console.log('Video or canvas ref missing');
      return;
    }
    
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');
    
    if (video.readyState === video.HAVE_ENOUGH_DATA) {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      context.drawImage(video, 0, 0, canvas.width, canvas.height);
      
      const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
      
      if (typeof window.jsQR !== 'undefined') {
        const code = window.jsQR(imageData.data, imageData.width, imageData.height, {
          inversionAttempts: "dontInvert",
        });
        
        if (code && code.data) {
          const url = code.data;
          console.log('QR Code detected:', url);
          
          if (url.includes('spotify.com') || url.includes('spotify:')) {
            const trackId = extractTrackId(url);
            console.log('Track ID extracted:', trackId);
            if (trackId) {
              playTrack(trackId);
            }
          } else {
            console.log('Not a Spotify URL:', url);
          }
        }
      } else {
        console.warn('jsQR library not loaded yet');
      }
    }
  };

  useEffect(() => {
    const loadJsQR = async () => {
      // Try multiple CDN sources
      const cdnUrls = [
        'https://cdn.jsdelivr.net/npm/jsqr@1.4.0/dist/jsQR.min.js',
        'https://unpkg.com/jsqr@1.4.0/dist/jsQR.min.js',
        'https://cdnjs.cloudflare.com/ajax/libs/jsqr/1.4.0/jsQR.min.js'
      ];

      for (const url of cdnUrls) {
        try {
          console.log('Trying to load jsQR from:', url);
          const script = document.createElement('script');
          script.src = url;
          
          await new Promise((resolve, reject) => {
            script.onload = () => {
              console.log('jsQR library loaded successfully from:', url);
              resolve();
            };
            script.onerror = () => {
              console.error('Failed to load from:', url);
              reject();
            };
            document.head.appendChild(script);
          });
          
          // If we get here, it loaded successfully
          return;
        } catch (err) {
          console.log('Trying next CDN...');
          continue;
        }
      }
      
      // If all CDNs failed
      console.error('Failed to load jsQR from all CDN sources');
      setError('Failed to load QR scanner library. Please refresh the page.');
    };

    loadJsQR();
    
    return () => {
      stopScanning();
    };
  }, []);

  if (!authenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-900 via-gray-900 to-black p-4 flex items-center justify-center">
        <div className="max-w-md w-full bg-gray-800 rounded-2xl shadow-2xl p-8">
          <div className="text-center">
            <div className="bg-green-500/10 rounded-full p-8 inline-block mb-6">
              <Music className="w-24 h-24 text-green-500" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-4">
              Spotify Song Guesser
            </h1>
            <p className="text-gray-400 mb-8">
              Scan QR codes to play songs on your Spotify app without revealing the track info. Perfect for guessing games!
            </p>
            
            {/* Debug info */}
            <div className="mb-4 bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
              <p className="text-blue-300 text-xs">
                Status: <strong>{authenticated ? '✅ Authenticated' : '❌ Not Authenticated'}</strong>
              </p>
              <p className="text-blue-300 text-xs mt-1">
                Client ID: <strong>{CLIENT_ID === 'YOUR_CLIENT_ID_HERE' ? '⚠️ NOT SET' : '✅ Set'}</strong>
              </p>
              <p className="text-blue-300 text-xs mt-1">
                Redirect URI: <strong>{REDIRECT_URI}</strong>
              </p>
            </div>

            <button
              onClick={loginToSpotify}
              disabled={CLIENT_ID === 'YOUR_CLIENT_ID_HERE'}
              className="w-full bg-green-500 hover:bg-green-600 text-white font-semibold px-8 py-4 rounded-full transition-all transform hover:scale-105 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Connect to Spotify
            </button>
            
            {CLIENT_ID === 'YOUR_CLIENT_ID_HERE' && (
              <p className="text-red-400 text-sm mt-3">⚠️ Please set your Spotify Client ID first</p>
            )}

            <div className="mt-6 bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
              <p className="text-yellow-400 text-sm font-semibold mb-2">⚠️ Requirements:</p>
              <ul className="text-yellow-300 text-xs text-left space-y-1">
                <li>• Spotify Premium account (required for playback control)</li>
                <li>• Spotify app open on your device</li>
                <li>• Play any song once to activate your device</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-900 via-gray-900 to-black p-4">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8 pt-8">
          <div className="flex items-center justify-center gap-3 mb-3">
            <Music className="w-10 h-10 text-green-500" />
            <h1 className="text-4xl font-bold text-white">Song Guesser</h1>
          </div>
          <p className="text-gray-400">Scan to play • No spoilers!</p>
          <button
            onClick={logout}
            className="mt-4 text-sm text-gray-500 hover:text-gray-300 underline"
          >
            Disconnect Spotify
          </button>
        </div>

        <div className="bg-gray-800 rounded-2xl shadow-2xl overflow-hidden">
          <div className="p-8">
            {!scanning ? (
              <div className="text-center">
                <div className="bg-green-500/10 rounded-full p-8 inline-block mb-6">
                  <Camera className="w-24 h-24 text-green-500" />
                </div>
                <h2 className="text-2xl font-semibold text-white mb-4">
                  Ready to Scan
                </h2>
                <p className="text-gray-400 mb-8">
                  Make sure Spotify is open and playing, then scan a QR code
                </p>
                <button
                  onClick={startScanning}
                  disabled={cameraLoading}
                  className="bg-green-500 hover:bg-green-600 text-white font-semibold px-8 py-4 rounded-full transition-all transform hover:scale-105 shadow-lg inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Camera className="w-5 h-5" />
                  {cameraLoading ? 'Starting Camera...' : 'Start Scanning'}
                </button>
              </div>
            ) : (
              <div>
                <div className="relative bg-black rounded-lg overflow-hidden mb-4">
                  <video
                    ref={videoRef}
                    className="w-full"
                    playsInline
                    autoPlay
                    muted
                    style={{ minHeight: '300px' }}
                  />
                  <canvas ref={canvasRef} className="hidden" />
                  
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <div className="border-4 border-green-500 rounded-lg w-64 h-64 animate-pulse" />
                  </div>

                  {playbackStarted && (
                    <div className="absolute inset-0 bg-green-500/90 flex items-center justify-center animate-fade-in">
                      <div className="text-center">
                        <CheckCircle className="w-20 h-20 text-white mx-auto mb-4 animate-bounce" />
                        <p className="text-white text-2xl font-bold">Song Changed!</p>
                        <p className="text-white/80 text-sm mt-2">Playing in background...</p>
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="text-center">
                  <p className="text-green-400 font-semibold mb-4">
                    🎵 Point camera at QR code
                  </p>
                  <button
                    onClick={stopScanning}
                    className="bg-red-500 hover:bg-red-600 text-white font-semibold px-6 py-3 rounded-full transition-all inline-flex items-center gap-2"
                  >
                    <XCircle className="w-5 h-5" />
                    Stop Scanning
                  </button>
                </div>
              </div>
            )}

            {error && (
              <div className="mt-6 bg-red-500/10 border border-red-500 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                  <p className="text-red-400 text-sm">{error}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="mt-6 bg-gray-800/50 rounded-lg p-4">
          <h3 className="text-white font-semibold mb-2">📱 Setup Instructions:</h3>
          <ol className="text-gray-400 text-sm space-y-1 list-decimal list-inside">
            <li>Open the Spotify app on your phone or computer</li>
            <li>Play any song briefly (this activates your device)</li>
            <li>You can minimize Spotify - it will stay in the background</li>
            <li>Come back here and scan QR codes to change songs</li>
            <li>The song changes silently - perfect for guessing!</li>
          </ol>
        </div>
      </div>
    </div>
  );
}